import { isPageHidden, pageVisibilityChangedListener, removePageVisibilityChangedListener } from './page-visibility';

export default class AppFocusHelper {
    constructor() {
        this.handlers = [];
        this.isAppFocused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        this.focusCheckInterval = null;
        this.onWindowBlur = this.onWindowBlur.bind(this);
        this.onWindowFocus = this.onWindowFocus.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    /**
     * Check if the app is in fullscreen mode (native or fake fullscreen)
     * @returns {boolean}
     */
    isInFullscreenMode() {
        // Check for fake fullscreen (used by webapp assets)
        if (document.body.classList.contains('fake-fullscreen')) {
            return true;
        }

        // Check for native fullscreen
        return !!(
            document.fullScreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement ||
            document.fullscreenElement
        );
    }

    /**
     * Check if there's a visible iframe in the document (not necessarily focused)
     * @returns {boolean}
     */
    hasVisibleIframe() {
        const iframes = document.getElementsByTagName('iframe');

        for (let i = 0; i < iframes.length; i++) {
            const iframe = iframes[i];
            const rect = iframe.getBoundingClientRect();
            const isVisible =
                rect.top < window.innerHeight &&
                rect.bottom > 0 &&
                rect.left < window.innerWidth &&
                rect.right > 0 &&
                rect.width > 0 &&
                rect.height > 0;

            if (isVisible) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if the active element is a visible iframe (webapp, URL, etc.)
     * This helps distinguish between actual blur (user left the page/tab)
     * and focus moving to an iframe within the page
     * @returns {boolean}
     */
    isActiveElementVisibleIframe() {
        const activeElement = document.activeElement;

        if (!activeElement || activeElement.tagName !== 'IFRAME') {
            return false;
        }

        // Check if the iframe is visible in the viewport
        const rect = activeElement.getBoundingClientRect();
        const isVisible =
            rect.top < window.innerHeight &&
            rect.bottom > 0 &&
            rect.left < window.innerWidth &&
            rect.right > 0 &&
            rect.width > 0 &&
            rect.height > 0;

        return isVisible;
    }

    addEventListeners() {
        if (typeof document.hasFocus === 'function') {
            this.focusCheckInterval = setInterval(() => {
                const documentHasFocus = document.hasFocus();
                const activeElementIsIframe = this.isActiveElementVisibleIframe();
                const isFullscreen = this.isInFullscreenMode();
                const hasVisibleIframe = this.hasVisibleIframe();

                // When document doesn't have focus (user clicked outside browser),
                // we should NOT consider the iframe as focused, even if it's the activeElement.
                // Only consider iframe focused if the document ALSO has focus (Firefox edge case).
                const isIframeFocused = activeElementIsIframe && documentHasFocus;

                // Fullscreen with iframe only prevents blur if we're actively interacting (document has focus)
                const isFullscreenWithIframe = isFullscreen && hasVisibleIframe && documentHasFocus;
                const shouldConsiderFocused = documentHasFocus || isIframeFocused || isFullscreenWithIframe;

                if (this.isAppFocused !== shouldConsiderFocused) {
                    this.isAppFocused = shouldConsiderFocused;
                    this.emit();
                }
            }, 2000);
        }

        window.addEventListener('blur', this.onWindowBlur);
        window.addEventListener('focus', this.onWindowFocus);

        pageVisibilityChangedListener(this.onVisibilityChange);
    }

    removeEventListeners() {
        clearInterval(this.focusCheckInterval);

        window.removeEventListener('blur', this.onWindowBlur);
        window.removeEventListener('focus', this.onWindowFocus);

        removePageVisibilityChangedListener(this.onVisibilityChange);
    }

    onWindowBlur() {
        // Use document.hasFocus to not take window.blur events into account when an iframe gets focus
        const documentHasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : false;

        const activeElementIsIframe = this.isActiveElementVisibleIframe();
        const isFullscreen = this.isInFullscreenMode();
        const hasVisibleIframe = this.hasVisibleIframe();

        // When in fullscreen with a visible iframe, ignore onWindowBlur entirely.
        // This is because in Firefox, clicking on the iframe triggers blur BEFORE the iframe
        // becomes activeElement and BEFORE documentHasFocus becomes true again.
        // The interval check will handle the actual state correctly after the transition completes.
        if (isFullscreen && hasVisibleIframe) {
            return;
        }

        // Firefox-specific behavior: When clicking on an iframe (e.g., webapp asset), Firefox reports
        // document.hasFocus() as false, while Chrome keeps it as true. To avoid pausing device events
        // when the user is still interacting with a visible iframe (like a fullscreen webapp asset),
        // we check if the active element is a visible iframe AND document has focus.
        const isIframeFocused = activeElementIsIframe && documentHasFocus;
        const shouldConsiderFocused = documentHasFocus || isIframeFocused;

        if (this.isAppFocused !== shouldConsiderFocused) {
            this.isAppFocused = shouldConsiderFocused;
            this.emit();
        }
    }

    onWindowFocus() {
        const documentHasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
        if (this.isAppFocused !== documentHasFocus) {
            this.isAppFocused = documentHasFocus;
            this.emit();
        }
    }

    /**
     * Handles page visibility changes using the Page Visibility API.
     *
     * This method is critical for detecting when users truly leave the page (tab switch, minimize, etc.)
     * vs. browser-specific fullscreen transitions that temporarily change visibility state.
     *
     * Key behaviors:
     *
     * 1. **Page becomes hidden (document.hidden = true)**:
     *    - Tab switch: User switched to another tab → Trigger blur
     *    - Window minimize: User minimized the browser → Trigger blur
     *    - Click outside: User clicked on another application → Trigger blur
     *    - Fullscreen transition (Firefox): Browser entering fullscreen mode → IGNORE (not a real blur)
     *
     * 2. **Page becomes visible (document.hidden = false)**:
     *    - User returns to the tab/window → Restore focus state based on current conditions
     *
     * Firefox-specific edge case:
     * When entering fullscreen mode, Firefox temporarily sets document.hidden to true, then to undefined.
     * This is NOT the user leaving the page, just a browser fullscreen transition.
     * We detect this by checking if we're in fullscreen with a visible iframe and ignore the event.
     *
     * Why this is needed:
     * - The Page Visibility API is more reliable than blur/focus events for detecting tab switches
     * - It works consistently across browsers for detecting when users truly leave the page
     * - But it requires special handling for fullscreen transitions to avoid false positives
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
     */
    onVisibilityChange() {
        const pageHidden = isPageHidden();
        const isFullscreen = this.isInFullscreenMode();
        const hasVisibleIframe = this.hasVisibleIframe();
        const isFullscreenWithIframe = isFullscreen && hasVisibleIframe;

        if (pageHidden) {
            // Firefox fires visibilitychange with hidden=true when entering fullscreen.
            // We must ignore this event if we're in fullscreen with a visible iframe, as
            // it's not the user leaving the page, just the browser's fullscreen transition.
            if (isFullscreenWithIframe) {
                return;
            }

            // Page is truly hidden (tab switched, window minimized, etc.)
            if (this.isAppFocused !== false) {
                this.isAppFocused = false;
                this.emit();
            }
        } else {
            // Page is visible again - restore focus state
            const documentHasFocus = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
            const isIframeFocused = this.isActiveElementVisibleIframe();

            // We use the same logic as interval check - fullscreen with iframe only keeps focus
            // if document also has focus. This prevents transient misstates when visibility changes
            // but document doesn't have focus (e.g., user clicked outside browser).
            const isFullscreenWithIframeAndFocus = isFullscreen && hasVisibleIframe && documentHasFocus;
            const shouldConsiderFocused = documentHasFocus || isIframeFocused || isFullscreenWithIframeAndFocus;

            if (this.isAppFocused !== shouldConsiderFocused) {
                this.isAppFocused = shouldConsiderFocused;
                this.emit();
            }
        }
    }

    emit() {
        this.handlers.forEach((handler) => {
            handler(this.isAppFocused);
        });
    }

    subscribe(handler) {
        if (this.handlers.length === 0) {
            this.addEventListeners();
        }
        this.handlers.push(handler);

        handler(this.isAppFocused);

        return { unsubscribe: () => this.unsubscribe(handler) };
    }

    unsubscribe(fn) {
        this.handlers = this.handlers.filter((handler) => {
            if (handler !== fn) {
                return handler;
            }
        });

        if (this.handlers.length === 0) {
            this.removeEventListeners();
        }
    }
}
